import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Radar,
  Search,
  Workflow,
  Activity,
  Sparkles,
  FileSearch,
  LineChart,
} from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { GlassIcon } from "@/components/glass-icon";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { MetricCounters } from "@/components/metric-counters";
import { OrbitLoop } from "@/components/orbit-loop";
import { TestimonialsMasonry } from "@/components/testimonials-masonry";
import { LandingHeader } from "@/components/landing-header";
import { BookDemoDialog } from "@/components/book-demo-dialog";
import { TrustStrip } from "@/components/trust-strip";
import { ProblemSplit } from "@/components/problem-split";
import { Input } from "@/components/ui/input";
import { seo } from "@/lib/seo";
import { breadcrumb, webPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { signUpUrl, scrollToSection } from "@/lib/cta";

const LP_PATH = "/lp/agencies";
const LP_TITLE = "Your SEO Clients Are About to Ask a New Question. Will You Have an Answer?";
const LP_DESCRIPTION =
  "Clients want to know how to show up in ChatGPT, Perplexity, Claude, and Gemini. Axy.digital helps agencies turn that into a high-margin, repeatable GEO service line — automated audits, citation tracking, and engine optimization.";

export const Route = createFileRoute("/lp/agencies")({
  head: () =>
    seo({
      title: LP_TITLE,
      description: LP_DESCRIPTION,
      path: LP_PATH,
      type: "website",
      jsonLd: [
        webPage({
          name: LP_TITLE,
          description: LP_DESCRIPTION,
          url: `${SITE_URL}${LP_PATH}`,
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: LP_TITLE, url: LP_PATH },
        ]),
      ],
    }),
  component: LandingPage,
});

const FINAL_SIGNUP = signUpUrl("lp-agencies-final");

const PILLARS = [
  {
    label: "Instant White-Label Audits",
    description:
      "When a client asks where they stand in AI search, don't guess. Axy.digital maps real-time conversational market behavior — tracking exactly where your client's brand is mentioned, where they're skipped, and which competitors are winning category share-of-voice. Export the data and bill it as a standalone strategy asset.",
    icon: FileSearch,
  },
  {
    label: "Autonomous Content Upgrades",
    description:
      "Turn data into execution without overwhelming your copywriting team. Axy.digital bypasses generic prompting by injecting your client's core brand parameters and live market narratives straight into automated execution pipelines — deploying the exact semantic content hubs needed to claim search dominance.",
    icon: Workflow,
  },
  {
    label: "Continuous Tracking & Retention Proof",
    description:
      "Traditional SEO reporting is broken. Axy.digital provides comprehensive performance tracking that visualizes your clients' citation footprint scaling over time across major LLMs. Hand your clients clean, undeniably technical growth metrics that justify your monthly retainer.",
    icon: LineChart,
  },
];

const FINAL_MATRIX = [
  {
    label: "Fulfillment Capacity",
    value: "Infinite Content Scale",
    proof: "Zero Copywriter Bottlenecks",
  },
  {
    label: "Margin Optimization",
    value: "90%+ Gross Margin",
    proof: "Automated Delivery Architecture",
  },
  {
    label: "Time-to-Market",
    value: "Ready to Sell Today",
    proof: "Turnkey Diagnostic Pitch Decks",
  },
];

function PitchAuditForm() {
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = new URL(signUpUrl("lp-agencies-pitch-audit"));
    if (domain) url.searchParams.set("domain", domain);
    if (email) url.searchParams.set("email", email);
    window.location.href = url.toString();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full">
      <Input
        type="text"
        required
        placeholder="clientdomain.com"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <Input
        type="email"
        required
        placeholder="agency@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <button
        type="submit"
        className="btn-gradient hover:brightness-110 transition-all rounded-full px-6 h-12 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap"
      >
        Run a Pitch-Ready GEO Audit
        <ArrowRight className="size-4" />
      </button>
    </form>
  );
}

function LandingPage() {
  // Header and hero CTAs share one modal, so `demo_cta_opened` and the Calendly
  // funnel fire identically from either.
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <>
      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
      <LandingHeader ctaLabel="Build Your Agency's GEO Offer" onClick={() => setDemoOpen(true)} />

      {/* HERO */}
      <section className="relative px-6 pt-20 pb-24 overflow-hidden">
        <AuroraBackground />
        <div
          aria-hidden
          className="absolute left-1/2 top-24 -translate-x-1/2 opacity-60 pointer-events-none"
        >
          <GlassOrb size="lg" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <SectionEyebrow className="justify-center text-center">
            Productize a high-margin GEO service line for your agency
          </SectionEyebrow>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white text-balance mb-6">
            Your SEO clients are about to ask a new question. Will you have an answer?
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto mb-10 text-pretty">
            Clients want to know how to show up in ChatGPT, Perplexity, Claude, and Gemini. Axy.digital
            helps agencies turn that question into a high-margin, repeatable GEO service line —
            automating visibility audits, citation tracking, and engine optimization.
          </p>
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Build Your Agency's GEO Offer
              <ArrowRight className="size-4" />
            </button>
            <p className="text-xs text-white/55">
              Engine live • Trusted by modern agencies • Book a 20-min strategy call
            </p>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* METRICS BANNER */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <SectionEyebrow>Agency-grade GEO fulfillment</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-3 max-w-3xl">
            Productize GEO without adding headcount — and protect every account on your roster.
          </h2>
          <p className="text-white/65 max-w-2xl mb-10">
            Built for SEO and growth agencies who need a backend fulfillment layer that delivers
            elite visibility campaigns at scale.
          </p>
          <MetricCounters
            items={[
              {
                value: 90,
                prefix: "+",
                suffix: "%",
                label: "Gross margin",
                proof: "On automated GEO retainers",
              },
              {
                value: 4,
                label: "LLMs tracked live",
                proof: "ChatGPT · Perplexity · Claude · Gemini",
              },
              {
                value: 100,
                suffix: "%",
                label: "Share-of-voice mapping",
                proof: "Real-time vs. your client's competitor set",
              },
              {
                value: 3,
                suffix: "×",
                label: "MRR per account",
                proof: "Adding GEO as a productized line",
              },
            ]}
          />
        </div>
      </section>

      {/* PROBLEM */}
      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>The Problem</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              The search conversation moved.{" "}
              <span className="text-gradient">Your stack didn't.</span>
            </h2>
            <p className="mt-4 text-lg text-white/70 max-w-2xl">
              Clients are asking about AI search visibility. Without a productized answer, you risk
              losing accounts to the first agency that can quote a GEO retainer with a straight
              face.
            </p>
          </div>
          <ProblemSplit />
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative px-6 py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 40% at 30% 20%, rgba(123,63,242,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <SectionEyebrow>Agency-grade GEO fulfillment</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6 max-w-3xl">
            The fulfillment layer behind your next high-margin retainer.
          </h2>
          <p className="text-white/75 leading-relaxed text-pretty max-w-3xl mb-12">
            Three always-on agents replace the analysts, writers, and reporting infrastructure you'd
            otherwise have to hire — so your team can focus on strategy and account growth.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => (
              <div
                key={p.label}
                className="glass-card p-6 hover-lift hover:-translate-y-1 relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <GlassIcon icon={p.icon} />
                  <span className="text-xs uppercase tracking-wider text-white/40 tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{p.label}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORBIT LOOP */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <SectionEyebrow className="justify-center">
            Turnkey solution for agencies — productize your GEO service line in 5 steps
          </SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance max-w-3xl mx-auto mb-4">
            From client discovery to margin expansion — fully automated.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            Axy.digital bridges the gap between deep algorithmic intelligence and programmatic fulfillment,
            letting your existing team deliver elite visibility campaigns across 5 automated phases.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <OrbitLoop
            steps={[
              {
                label: "Map Intent",
                description:
                  "The engine tracks live conversational trends to map out the exact, high-intent strategic prompts your client's buyers are actively typing into AI engines.",
                icon: Radar,
              },
              {
                label: "Analyze Gaps",
                description:
                  "Instantly expose where the client's brand is missing from the conversation and show them a precise roadmap of the category share-of-voice they're losing to competitors.",
                icon: Search,
              },
              {
                label: "Programmatic Execution",
                description:
                  "Automatically deploy optimized content assets, technical data schemas, and authority hubs required to win LLM citations — without writing a single line of script or middleware.",
                icon: Workflow,
              },
              {
                label: "Continuous Tracking",
                description:
                  "Provide absolute clarity to your accounts with historical performance tracking dashboards that clearly demonstrate their digital visibility scale under your management.",
                icon: Activity,
              },
              {
                label: "Infinite Scale",
                description:
                  "Run an always-on infrastructure layer that dynamically optimizes active campaigns based on weekly model updates — expanding your agency's delivery capacity without adding headcount.",
                icon: Sparkles,
              },
            ]}
          />
          <div className="mt-8 flex justify-center">
            <span className="glass-pill px-4 py-2 text-xs text-white/85 inline-flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />0 manual steps ·
              White-label fulfillment running on autopilot
            </span>
          </div>
        </div>
      </section>

      {/* PITCH AUDIT — SECONDARY CTA */}
      <section id="agency-audit-zone" className="px-6 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 md:p-12">
            <SectionEyebrow>Pitch-ready GEO audit</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-4">
              🎯 Stop letting other agencies win the modern search conversation.
            </h2>
            <p className="text-white/70 max-w-2xl mb-8 text-pretty">
              Enter a client's (or target's) domain below and our engine will instantly map out
              their active citation gaps and AI blindspots — ready to drop into your next pitch
              deck.
            </p>
            <PitchAuditForm />
            <p className="mt-4 text-xs text-white/50">
              We'll email your pitch-ready GEO audit within minutes.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsMasonry
        eyebrow="Agencies growing with Axy.digital"
        heading="Modern agencies productizing GEO with Axy.digital."
        items={[
          {
            quote:
              "Axy.digital helped us 5x our content output without sounding generic. The content is detailed, sharp, and actually reflects how we want to show up as an early-stage team.",
            name: "Aurelien Vasinis",
            role: "Founder & CEO",
            company: "Kuration AI",
            initials: "AV",
            hue: 295,
            metric: { value: "60+ hrs", label: "saved monthly" },
            featured: true,
          },
          {
            quote:
              "In 90 days, Axy.digital helped us go from invisible in AI answers to ranking #3 against incumbents with a decade of SEO dominance.",
            name: "Savannah Thatcher",
            role: "CMO",
            company: "Wingbits",
            initials: "ST",
            hue: 320,
            metric: { value: "11×", label: "AI visibility growth" },
          },
          {
            quote:
              "The depth of the content Axy.digital generated from very little input was genuinely impressive. Not having to prompt or configure everything manually was a game changer.",
            name: "Florian Rennberger",
            role: "Head of Sales",
            company: "Conducttr Germany",
            initials: "FR",
            hue: 270,
          },
        ]}
      />

      {/* FINAL CTA */}
      <section className="relative px-6 py-24 border-t border-neutral-800 bg-neutral-950 overflow-hidden">
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-mono">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
              </span>
              White-Label Fulfillment Engine: Online
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance mb-5">
            Own the Category Before Your Competitors Productize It.
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-12 text-pretty">
            Stop waiting for your clients to look elsewhere for AI optimization strategies. Turn
            conversational search disruption into your highest-margin service line, automate your
            backend fulfillment, and scale your agency's MRR effortlessly.
          </p>

          {/* Agency Leverage Matrix */}
          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 rounded-2xl overflow-hidden mb-12 bg-white/[0.02]">
            {FINAL_MATRIX.map((m, i) => (
              <div
                key={m.label}
                className={[
                  "px-5 py-6 text-left",
                  i < FINAL_MATRIX.length - 1 ? "md:border-r border-white/10" : "",
                  i < FINAL_MATRIX.length - 1 ? "border-b md:border-b-0" : "",
                ].join(" ")}
              >
                <div className="text-xs uppercase tracking-wider text-white/45 mb-2">{m.label}</div>
                <div className="text-2xl md:text-3xl font-semibold text-white mb-1">{m.value}</div>
                <div className="text-xs text-neutral-400">{m.proof}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            <a
              href={FINAL_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-7 py-3.5 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Build Your Agency's GEO Offer (Free · 2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              Free agency tier includes 3 complimentary live market diagnostic maps. Protect your
              accounts and land new business instantly.
            </p>
          </div>

          <div className="mt-10 text-sm text-white/55">
            Want to generate a test diagnostic report for a pitch target right now?{" "}
            <button
              type="button"
              onClick={() => scrollToSection("agency-audit-zone")}
              className="text-white/85 underline underline-offset-4 hover:text-white transition-colors"
            >
              Run Pitch-Ready GEO Audit
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
